import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { format, isValid, parse } from "date-fns";
import { enqueueSnackbar } from "notistack";
import { EditAccountTablePagination } from "../../components/AccountDetailPolicy/editAccountTableControls";
import {
  archivePartnerProgram,
  retirePartnerProgram,
} from "../../helpers/partnerCentral_helper";
import { HeaderTip } from "../Compliance/AuditHub/components/AuditCells";
import {
  AH_ACCENT,
  AH_BORDER,
  AH_MUTED,
  AH_TEXT,
  AH_WHITE,
  auditHubPageSx,
  searchFieldSx,
  tableShellSx,
} from "../Compliance/AuditHub/theme";
import ProgramDetailsDialog from "./ProgramDetailsDialog";

const ADD_PROGRAM_PATH = "/partner-central/dashboard/add-program";

const TYPE_LABEL = {
  EDP: "EDP",
  PPA: "PPA",
  SPP: "SPP",
  RI_SP: "RI",
};

/** Same header treatment as Audit Hub AuditsTable. */
const HEAD_CELL_SX = {
  fontSize: "0.7rem",
  fontWeight: 700,
  letterSpacing: "0.04em",
  color: AH_MUTED,
  backgroundColor: AH_WHITE,
  borderBottom: `1px solid ${AH_BORDER}`,
  whiteSpace: "nowrap",
  lineHeight: 1.25,
  py: 1.25,
  px: 0.75,
  textAlign: "center",
};

/** Same body treatment as Audit Hub / Configuration tables. */
const BODY_CELL_SX = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  lineHeight: 1.4,
  color: "#334155",
  borderBottom: `1px solid ${AH_BORDER}`,
  py: 1.5,
  px: 0.75,
  verticalAlign: "middle",
  textAlign: "center",
};

const ACTION_BTN_SX = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#f8fafc",
  color: "#64748b",
  padding: 0,
  flexShrink: 0,
  "&:hover": {
    backgroundColor: "#f1f5f9",
    color: "#334155",
  },
  "&.Mui-disabled": {
    color: "#cbd5e1",
    borderColor: "#f1f5f9",
    backgroundColor: "#f8fafc",
  },
};

const DELETE_BTN_SX = {
  ...ACTION_BTN_SX,
  color: "#dc2626",
  borderColor: "#fecaca",
  backgroundColor: "#fef2f2",
  "&:hover": {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
  },
  "&.Mui-disabled": {
    color: "#fca5a5",
    borderColor: "#fee2e2",
    backgroundColor: "#fff",
  },
};

const dialogButtonSx = {
  minWidth: 140,
  height: 38,
  px: 2,
  borderRadius: "4px",
  textTransform: "none",
  fontWeight: 400,
  fontSize: "0.875rem",
  boxShadow: "none",
  "&:hover": { boxShadow: "none" },
};

/** Status tones match Audit Hub AuditStatusChip (RETIRED = Completed blue). */
const STATUS_TONE = {
  ACTIVE: { bg: "#ecfdf5", fg: "#047857" },
  DRAFT: { bg: "#fffbeb", fg: "#b45309" },
  RETIRED: { bg: "#eff6ff", fg: "#2563eb" },
  ARCHIVED: { bg: "#f1f5f9", fg: "#475569" },
};

const COLUMNS = [
  { key: "name", label: "AWS DISCOUNT PROGRAM", tip: "Display name of the AWS discount program." },
  { key: "type", label: "TYPE", tip: "Program type: EDP, PPA, SPP, or RI." },
  { key: "agreementId", label: "AGREEMENT ID", tip: "Agreement or contract identifier." },
  { key: "status", label: "STATUS", tip: "Current program status." },
  { key: "startDate", label: "START DATE", tip: "Program start date." },
  { key: "endDate", label: "END DATE", tip: "Program end date." },
  { key: "createdAt", label: "CREATED", tip: "When the program record was created." },
];

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = parse(String(value).slice(0, 10), "yyyy-MM-dd", new Date());
  return isValid(parsed) ? format(parsed, "MM/dd/yyyy") : String(value);
};

const formatCreatedAt = (value) => {
  if (!value) return "—";
  const normalized = String(value).includes("T") ? value : String(value).replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return String(value);
  return format(date, "MM/dd/yyyy");
};

const dateSortMs = (value) => {
  if (!value) return 0;
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const parsed = parse(raw.slice(0, 10), "yyyy-MM-dd", new Date());
    return isValid(parsed) ? parsed.getTime() : 0;
  }
  const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
  const ms = Date.parse(normalized);
  return Number.isFinite(ms) ? ms : 0;
};

const sortValue = (program, field) => {
  switch (field) {
    case "name":
      return String(program.program_display_name || "").toLowerCase();
    case "type":
      return String(TYPE_LABEL[program.program_type] || program.program_type || "").toLowerCase();
    case "agreementId":
      return String(program.agreement_id || "").toLowerCase();
    case "status":
      return String(program.status || "").toLowerCase();
    case "startDate":
      return dateSortMs(program.start_date);
    case "endDate":
      return dateSortMs(program.end_date);
    case "createdAt":
      return dateSortMs(program.created_at);
    default:
      return "";
  }
};

const matchesQuery = (program, query) => {
  if (!query) return true;
  const haystack = [
    program.program_display_name,
    program.agreement_id,
    program.program_type,
    TYPE_LABEL[program.program_type],
    program.status,
    program.reference_number,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
};

const statusChipSx = (status) => {
  const tone = STATUS_TONE[status] || STATUS_TONE.ARCHIVED;
  return {
    height: 22,
    fontSize: "0.75rem",
    fontWeight: 600,
    borderRadius: "6px",
    backgroundColor: tone.bg,
    color: tone.fg,
    "& .MuiChip-label": { px: 1 },
  };
};

const programLabel = (program) =>
  program?.program_display_name || program?.agreement_id || "this program";

const ProgramsGrid = ({
  programs,
  loading,
  error,
  onRefresh,
  title = "AWS discount programs",
  emptyMessage = "No draft or active programs yet.",
  showRetire = true,
  tableAriaLabel = "Partner Central programs",
  searchPlaceholder = "Search by name, agreement ID or type",
  /** When true, omit outer gray shell — parent already provides Audit Hub tableShellSx. */
  embedded = false,
  sx = null,
}) => {
  const history = useHistory();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [query, setQuery] = useState("");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [order, setOrder] = useState("desc");
  const [busyKey, setBusyKey] = useState(null);
  const [viewTarget, setViewTarget] = useState(null);
  const [retireTarget, setRetireTarget] = useState(null);
  const [retiring, setRetiring] = useState(false);
  const [retireError, setRetireError] = useState("");

  useEffect(() => {
    setPage(0);
  }, [programs, query, orderBy, order]);

  useEffect(() => {
    if (!retireTarget) {
      setRetiring(false);
      setRetireError("");
    }
  }, [retireTarget]);

  const filteredPrograms = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    const matched = normalized
      ? programs.filter((program) => matchesQuery(program, normalized))
      : programs;
    const sorted = [...matched].sort((left, right) => {
      const direction = order === "asc" ? 1 : -1;
      const a = sortValue(left, orderBy);
      const b = sortValue(right, orderBy);
      if (a < b) return -1 * direction;
      if (a > b) return 1 * direction;
      return String(right.id || "").localeCompare(String(left.id || ""));
    });
    return sorted;
  }, [programs, query, orderBy, order]);

  const rows = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredPrograms.slice(start, start + rowsPerPage);
  }, [filteredPrograms, page, rowsPerPage]);

  const handleSort = (field) => {
    if (orderBy === field) {
      setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setOrderBy(field);
    setOrder("asc");
  };

  const openEdit = (program) => {
    if (!program?.id || !program?.program_type) return;
    history.push(
      `${ADD_PROGRAM_PATH}?id=${encodeURIComponent(program.id)}&program_type=${encodeURIComponent(program.program_type)}`
    );
  };

  const closeRetireDialog = () => {
    if (retiring) return;
    setRetireTarget(null);
  };

  const confirmRetire = async () => {
    const program = retireTarget;
    if (!program?.id || !program?.program_type || retiring) return;
    if (program.status !== "ACTIVE" || program.is_retired === true) {
      setRetireError("Only active programs can be retired.");
      return;
    }
    const key = `${program.program_type}-${program.id}`;
    const label = programLabel(program);
    setRetiring(true);
    setRetireError("");
    setBusyKey(key);
    try {
      await retirePartnerProgram(program.program_type, program.id, true);
      enqueueSnackbar(`Program retired: ${label}.`, { variant: "success" });
      setRetireTarget(null);
      if (typeof onRefresh === "function") onRefresh();
    } catch (err) {
      setRetireError(err?.message || "Unable to retire program.");
    } finally {
      setRetiring(false);
      setBusyKey(null);
    }
  };

  const handleDelete = async (program) => {
    if (!program?.id || !program?.program_type || busyKey) return;
    const label = programLabel(program);
    if (!window.confirm(`Archive ${label}? This removes it from the active programs list.`)) {
      return;
    }
    const key = `${program.program_type}-${program.id}`;
    setBusyKey(key);
    try {
      await archivePartnerProgram(program.program_type, program.id);
      enqueueSnackbar(`Program archived: ${label}.`, { variant: "success" });
      if (typeof onRefresh === "function") onRefresh();
    } catch (err) {
      enqueueSnackbar(err?.message || "Unable to archive program.", { variant: "error" });
    } finally {
      setBusyKey(null);
    }
  };

  const searching = Boolean(query.trim());
  const emptyText = searching ? "No programs match your search." : emptyMessage;

  const content = (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          mb: 1.75,
          flexWrap: "wrap",
        }}
      >
        <Typography
          component="h2"
          sx={{
            m: 0,
            fontWeight: 700,
            fontSize: "1rem",
            color: AH_TEXT,
            letterSpacing: "-0.01em",
            flexShrink: 0,
          }}
        >
          {title}
        </Typography>
        <TextField
          size="small"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={searchPlaceholder}
          aria-label={`Search ${title}`}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            ...searchFieldSx,
            flex: { xs: "1 1 160px", sm: "0 0 auto" },
            width: { xs: "auto", sm: 280 },
            minWidth: { xs: 0, sm: 280 },
          }}
        />
      </Box>

      {error ? (
        <Typography sx={{ fontSize: 13, color: "#d32f2f", py: 2 }} role="alert">
          {error}
        </Typography>
      ) : null}

      <Box
        sx={{
          border: `1px solid ${AH_BORDER}`,
          borderRadius: "12px",
          backgroundColor: AH_WHITE,
          overflow: "hidden",
        }}
      >
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" aria-label={tableAriaLabel} sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableCell key={column.key} sx={HEAD_CELL_SX}>
                    <HeaderTip
                      label={column.label}
                      title={column.tip}
                      sortField={column.key}
                      orderBy={orderBy}
                      order={order}
                      onSort={handleSort}
                    />
                  </TableCell>
                ))}
                <TableCell sx={HEAD_CELL_SX}>ACTIONS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ ...BODY_CELL_SX, py: 6, borderBottom: 0 }}>
                    <CircularProgress size={24} sx={{ color: AH_ACCENT }} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ py: 6, borderBottom: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        color: AH_MUTED,
                      }}
                    >
                      <SearchOffIcon sx={{ fontSize: 40, mb: 1, color: "#cbd5e1" }} />
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: AH_TEXT }}>
                        {emptyText}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((program) => {
                  const rowKey = `${program.program_type}-${program.id}`;
                  const rowBusy = busyKey === rowKey;
                  const canRetire =
                    showRetire &&
                    program.status === "ACTIVE" &&
                    program.is_retired !== true;
                  return (
                    <TableRow key={rowKey} hover>
                      <TableCell sx={{ ...BODY_CELL_SX, fontWeight: 600, color: AH_TEXT, minWidth: 180 }}>
                        {program.program_display_name ? (
                          <Box
                            component="button"
                            type="button"
                            onClick={() => setViewTarget(program)}
                            aria-label={`View ${program.program_display_name}`}
                            sx={{
                              p: 0,
                              m: 0,
                              border: "none",
                              background: "transparent",
                              color: AH_TEXT,
                              font: "inherit",
                              fontWeight: 600,
                              fontSize: "0.8125rem",
                              lineHeight: 1.4,
                              textDecoration: "none",
                              cursor: "pointer",
                              textAlign: "center",
                              maxWidth: "100%",
                              overflowWrap: "anywhere",
                              "&:hover": { color: AH_TEXT, textDecoration: "underline" },
                            }}
                          >
                            {program.program_display_name}
                          </Box>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        {TYPE_LABEL[program.program_type] || program.program_type || "—"}
                      </TableCell>
                      <TableCell sx={{ ...BODY_CELL_SX, minWidth: 140 }}>
                        {program.agreement_id || "—"}
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>
                        <Chip
                          size="small"
                          label={program.status || "—"}
                          sx={statusChipSx(program.status)}
                        />
                      </TableCell>
                      <TableCell sx={BODY_CELL_SX}>{formatDate(program.start_date)}</TableCell>
                      <TableCell sx={BODY_CELL_SX}>{formatDate(program.end_date)}</TableCell>
                      <TableCell sx={BODY_CELL_SX}>{formatCreatedAt(program.created_at)}</TableCell>
                      <TableCell sx={{ ...BODY_CELL_SX, whiteSpace: "nowrap", width: showRetire ? 120 : 88 }}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                            width: "100%",
                          }}
                        >
                          <Tooltip title="Edit program">
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Edit ${program.program_display_name || "program"}`}
                                disabled={Boolean(busyKey)}
                                onClick={() => openEdit(program)}
                                sx={ACTION_BTN_SX}
                              >
                                <EditOutlinedIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {canRetire ? (
                            <Tooltip title="Retire program">
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={`Retire ${program.program_display_name || "program"}`}
                                  disabled={Boolean(busyKey)}
                                  onClick={() => setRetireTarget(program)}
                                  sx={ACTION_BTN_SX}
                                >
                                  {rowBusy && retireTarget?.id === program.id ? (
                                    <CircularProgress size={14} sx={{ color: "#64748b" }} />
                                  ) : (
                                    <Inventory2OutlinedIcon sx={{ fontSize: 15 }} />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : null}
                          <Tooltip title="Archive program">
                            <span>
                              <IconButton
                                size="small"
                                aria-label={`Archive ${program.program_display_name || "program"}`}
                                disabled={Boolean(busyKey)}
                                onClick={() => handleDelete(program)}
                                sx={DELETE_BTN_SX}
                              >
                                {rowBusy && !retireTarget ? (
                                  <CircularProgress size={14} sx={{ color: "#fca5a5" }} />
                                ) : (
                                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {!loading && !error ? (
          <EditAccountTablePagination
            page={page}
            rowsPerPage={rowsPerPage}
            total={filteredPrograms.length}
            rowsPerPageOptions={[5, 10, 25]}
            onPageChange={setPage}
            onRowsPerPageChange={(size) => {
              setRowsPerPage(size);
              setPage(0);
            }}
          />
        ) : null}
      </Box>

      <ProgramDetailsDialog
        open={Boolean(viewTarget)}
        program={viewTarget}
        onClose={() => setViewTarget(null)}
      />

      <Dialog
        open={Boolean(retireTarget)}
        onClose={closeRetireDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { ...auditHubPageSx, borderRadius: "12px", overflow: "hidden", maxWidth: 560 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            minHeight: 56,
            px: 3,
            backgroundColor: AH_ACCENT,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "1.0625rem", color: AH_WHITE, lineHeight: 1.3 }}>
            Retire Program
          </Typography>
          <Box
            component="button"
            type="button"
            aria-label="Close"
            disabled={retiring}
            onClick={closeRetireDialog}
            sx={{
              p: 0,
              m: 0,
              border: "none",
              background: "transparent",
              color: AH_WHITE,
              fontSize: 28,
              fontWeight: 700,
              lineHeight: 1,
              cursor: retiring ? "default" : "pointer",
            }}
          >
            ×
          </Box>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5, backgroundColor: AH_WHITE }}>
          <Typography sx={{ fontSize: "0.875rem", color: AH_TEXT, lineHeight: 1.6 }}>
            Retire{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {programLabel(retireTarget)}
            </Box>
            ?
          </Typography>
          <Typography sx={{ fontSize: "0.8125rem", color: "#64748b", lineHeight: 1.6, mt: 1 }}>
            This sets the program status to RETIRED and removes it from the active programs list.
          </Typography>
        </DialogContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: { xs: "wrap", sm: "nowrap" },
            gap: 1.5,
            px: 3,
            py: 2,
            borderTop: `1px solid ${AH_BORDER}`,
            backgroundColor: "#f8fafc",
          }}
        >
          <Button
            onClick={closeRetireDialog}
            disabled={retiring}
            sx={{
              ...dialogButtonSx,
              backgroundColor: "#74788d",
              color: "#eff2f7",
              "&:hover": { backgroundColor: "#636678", boxShadow: "none" },
            }}
          >
            Cancel
          </Button>
          {retireError ? (
            <Typography
              role="alert"
              sx={{
                flex: 1,
                minWidth: 0,
                order: { xs: 3, sm: 0 },
                flexBasis: { xs: "100%", sm: "auto" },
                fontSize: "0.8125rem",
                lineHeight: 1.45,
                color: "#dc2626",
                textAlign: "center",
                overflowWrap: "anywhere",
              }}
            >
              {retireError}
            </Typography>
          ) : null}
          <Button
            onClick={confirmRetire}
            disabled={retiring || !retireTarget?.id}
            sx={{
              ...dialogButtonSx,
              backgroundColor: AH_ACCENT,
              color: AH_WHITE,
              "&:hover": { backgroundColor: "#e8741c", boxShadow: "none" },
              "&.Mui-disabled": { backgroundColor: AH_ACCENT, color: AH_WHITE, opacity: 0.65 },
            }}
          >
            {retiring ? "Retiring…" : "Retire"}
          </Button>
        </Box>
      </Dialog>
    </>
  );

  if (embedded) {
    return (
      <Box sx={{ width: "100%", mb: 3, boxSizing: "border-box", ...sx }}>
        {content}
      </Box>
    );
  }

  return <Box sx={{ ...tableShellSx, ...sx }}>{content}</Box>;
};

export default ProgramsGrid;
